<template>
  <el-container class="law-enforcement-container">
    <!-- 页面标题 -->
    <!-- <div class="page-header">
      <h2>作业记录仪视频离线分析</h2>
    </div> -->

    <!-- 核心功能区 -->
    <el-main class="main-content">
      <!-- 第一步：视频上传区域 -->
      <div class="upload-section card">
        <div class="section-title">
          <el-icon><Upload /></el-icon>
          视频文件上传
        </div>
        <div class="upload-content">
          <el-upload
            class="upload-demo"
            drag
            :auto-upload="false"
            :on-change="handleFileChange"
            :file-list="fileList"
            :multiple="true"
            :limit="10"
            :on-exceed="handleExceed"
            accept=".mp4,.avi,.mov,.mkv"
          >
            <el-icon class="el-icon--upload"><UploadFilled /></el-icon>
            <div class="el-upload__text">
              将视频文件拖到此处，或<em>点击上传</em>
            </div>
            <div class="el-upload__tip" slot="tip">
              支持格式：mp4、avi、mov、mkv，单文件最大500MB，最多同时上传10个文件
            </div>
          </el-upload>

          <!-- 手动上传按钮 -->
          <div class="manual-upload-btn" v-if="fileList.length > 0">
            <el-button type="primary" @click="handleManualUpload">
              确认上传文件
            </el-button>
          </div>

          <!-- 测试数据快捷按钮 -->
          <!-- <div class="test-data-btn" style="margin: 10px 0">
            <el-button type="warning" @click="loadTestData"
              >加载测试数据</el-button
            >
          </div> -->

          <!-- 上传文件列表 -->
          <div class="upload-file-list" v-if="uploadFiles.length > 0">
            <el-table :data="uploadFiles" border style="width: 100%">
              <el-table-column
                prop="name"
                label="文件名"
                show-overflow-tooltip
              />
              <el-table-column prop="size" label="文件大小(MB)" width="120">
                <template #default="scope">
                  {{ (scope.row.size / 1024 / 1024).toFixed(2) }}
                </template>
              </el-table-column>
              <el-table-column prop="status" label="上传状态" width="120">
                <template #default="scope">
                  <el-tag
                    :type="
                      scope.row.status === 'success' ? 'success' : 'warning'
                    "
                  >
                    {{ scope.row.status === "success" ? "已完成" : "上传中" }}
                  </el-tag>
                </template>
              </el-table-column>
              <el-table-column label="操作" width="80">
                <template #default="scope">
                  <el-button
                    type="text"
                    size="small"
                    @click="handleRemoveFile(scope.row)"
                    >删除</el-button
                  >
                </template>
              </el-table-column>
            </el-table>
          </div>

          <!-- 开始分析按钮 -->
          <div class="upload-actions" v-if="uploadFiles.length > 0">
            <el-button
              type="primary"
              @click="startAnalysis"
              :disabled="!hasUploadedFiles"
            >
              开始智能分析
            </el-button>
            <el-button type="info" @click="clearAllFiles">清空列表</el-button>
          </div>
        </div>
      </div>

      <!-- 第二步：分析进度区域 -->
      <div class="analysis-section card" v-if="analysisVisible">
        <div class="section-title">
          <el-icon><Clock /></el-icon>
          智能分析进度
        </div>
        <div class="analysis-content">
          <div
            v-for="(task, index) in analysisTasks"
            :key="index"
            class="analysis-task"
          >
            <div class="task-header">
              <span class="task-name">{{ task.name }}</span>
              <span class="task-percent">{{ task.progress }}%</span>
            </div>
            <el-progress :percentage="task.progress" :status="task.status" />
            <div class="task-status" v-if="task.status === 'success'">
              <el-tag type="success"
                >分析完成，发现{{ task.violationCount }}处违章</el-tag
              >
            </div>
            <div class="task-status" v-if="task.status === 'exception'">
              <el-tag type="danger">分析失败：{{ task.errorMsg }}</el-tag>
            </div>
          </div>

          <!-- 分析结果操作 -->
          <div class="analysis-actions" v-if="allAnalysisComplete">
            <el-button type="primary" @click="generateReport"
              >生成统计报告</el-button
            >
          </div>
        </div>
      </div>

      <!-- 第三步：分析结果展示 -->
      <div class="result-section card" v-if="resultVisible">
        <div class="section-title">
          <el-icon><Document /></el-icon>
          分析结果与报告
        </div>
        <div class="result-content">
          <!-- 违章截图展示 -->
          <div class="violation-images">
            <div class="section-subtitle">违章截图</div>
            <div class="image-grid">
              <div
                v-for="(img, index) in violationImages"
                :key="index"
                class="image-item"
              >
                <el-image
                  :src="img.url"
                  :preview-src-list="violationImages.map((item) => item.url)"
                  fit="cover"
                />
                <div class="image-desc">{{ img.desc }}</div>
                <div class="image-time">{{ img.time }}</div>
              </div>
            </div>
          </div>

          <!-- 违章视频回放 -->
          <div class="violation-video">
            <div class="section-subtitle">违章视频片段</div>
            <video
              controls
              width="100%"
              poster="https://picsum.photos/1200/600"
              class="video-player"
            >
              <source :src="violationVideoUrl" type="video/mp4" />
              您的浏览器不支持HTML5视频播放
            </video>
          </div>

          <!-- 统计报告 -->
          <div class="report-section">
            <div class="section-subtitle">统计报告</div>
            <div class="report-card">
              <div class="report-header">
                <h3>作业记录仪视频分析报告</h3>
                <div class="report-date">生成时间：{{ reportDate }}</div>
              </div>
              <div class="report-content">
                <!-- Element Plus 表格 -->
                <el-table
                  :data="reportTableData"
                  border
                  size="small"
                  style="width: 100%; margin-bottom: 16px"
                >
                  <el-table-column
                    prop="label"
                    label="项目"
                    width="120"
                    align="center"
                  />
                  <el-table-column prop="value" label="内容" />
                </el-table>

                <div class="report-desc">
                  <h4>违章详情说明：</h4>
                  <p>{{ reportData.detailDesc }}</p>
                </div>
              </div>
            </div>

            <!-- 报告操作 -->
            <div class="report-actions">
              <el-button type="primary" @click="shareToIm">
                <el-icon><Share /></el-icon>
                分享至中油即时通
              </el-button>
              <el-button type="primary" @click="downloadReport">
                <el-icon><Download /></el-icon>
                下载报告
              </el-button>
              <el-button type="primary" @click="printReport">
                <el-icon><Printer /></el-icon>
                打印报告
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </el-main>
  </el-container>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from "vue";
import {
  Upload,
  UploadFilled,
  Clock,
  Document,
  Share,
  Download,
  Printer,
  Delete,
} from "@element-plus/icons-vue";
import { ElMessage, ElMessageBox } from "element-plus";

// 原始文件列表（Upload组件绑定）
const fileList = ref([]);
// 处理后的上传文件列表（核心测试数据）
const uploadFiles = ref([
  {
    name: "20250102_100000.mp4",
    size: 256 * 1024 * 1024, // 256MB
    status: "success",
    raw: {},
  },
  {
    name: "20250102_113000.mp4",
    size: 320 * 1024 * 1024, // 320MB
    status: "success",
    raw: {},
  },
  {
    name: "20250102_141500.mp4",
    size: 180 * 1024 * 1024, // 180MB
    status: "success",
    raw: {},
  },
]);
// 分析区域显示状态
const analysisVisible = ref(false);
// 结果区域显示状态
const resultVisible = ref(false);
// 分析任务列表
const analysisTasks = ref([]);
// 违章截图列表
const violationImages = ref([]);
// 违章视频地址
const violationVideoUrl = ref("https://www.w3school.com.cn/i/movie.mp4");
// 报告生成时间
const reportDate = ref("");
// 报告数据
const reportData = ref({});
// 报告表格数据
const reportTableData = ref([]);

// 计算属性：是否有已上传完成的文件
const hasUploadedFiles = computed(() => {
  return uploadFiles.value.some((file) => file.status === "success");
});

// 计算属性：所有分析任务是否完成
const allAnalysisComplete = computed(() => {
  if (analysisTasks.value.length === 0) return false;
  return analysisTasks.value.every(
    (task) => task.status === "success" || task.status === "exception",
  );
});

// 加载测试数据（核心新增）
const loadTestData = () => {
  // 模拟3个已上传完成的视频文件
  uploadFiles.value = [
    {
      name: "20250102_100000.mp4",
      size: 256 * 1024 * 1024, // 256MB
      status: "success",
      raw: {},
    },
    {
      name: "20250102_113000.mp4",
      size: 320 * 1024 * 1024, // 320MB
      status: "success",
      raw: {},
    },
    {
      name: "20250102_141500.mp4",
      size: 180 * 1024 * 1024, // 180MB
      status: "success",
      raw: {},
    },
  ];

  // 同步原始文件列表
  fileList.value = uploadFiles.value.map((file) => ({
    name: file.name,
    size: file.size,
    status: file.status,
  }));

  ElMessage.success('测试数据加载完成！可直接点击"开始智能分析"');
};

// 文件选择变化处理
const handleFileChange = (file, fileList) => {
  fileList.value = fileList;
};

// 文件上传超出限制处理
const handleExceed = (files, uploadFiles) => {
  ElMessage.warning(
    `最多只能上传10个文件，本次选择了${files.length}个，已超出限制。`,
  );
};

// 手动上传文件（前端模拟）
const handleManualUpload = () => {
  if (fileList.value.length === 0) {
    ElMessage.warning("请先选择要上传的文件！");
    return;
  }

  uploadFiles.value = [];

  fileList.value.forEach((file) => {
    const fileItem = {
      name: file.name,
      size: file.size,
      status: "uploading",
      raw: file.raw || {},
    };
    uploadFiles.value.push(fileItem);

    // 模拟上传延迟
    setTimeout(() => {
      fileItem.status = "success";
      ElMessage.success(`${file.name} 上传成功！`);
    }, Math.random() * 2000 + 1000);
  });

  ElMessage.info("开始上传文件，请稍候...");
};

// 移除单个文件
const handleRemoveFile = (file) => {
  const index = uploadFiles.value.findIndex((item) => item.name === file.name);
  if (index > -1) {
    uploadFiles.value.splice(index, 1);
  }

  const rawIndex = fileList.value.findIndex((item) => item.name === file.name);
  if (rawIndex > -1) {
    fileList.value.splice(rawIndex, 1);
  }
};

// 清空所有文件
const clearAllFiles = () => {
  ElMessageBox.confirm("确定要清空所有上传文件吗？", "提示", {
    confirmButtonText: "确定",
    cancelButtonText: "取消",
    type: "warning",
  }).then(() => {
    uploadFiles.value = [];
    fileList.value = [];
    analysisVisible.value = false;
    resultVisible.value = false;
    ElMessage.success("已清空文件列表");
  });
};

// 开始智能分析（模拟进度）
const startAnalysis = () => {
  analysisVisible.value = true;
  resultVisible.value = false;

  // 初始化分析任务
  analysisTasks.value = uploadFiles.value.map((file) => ({
    name: file.name,
    progress: 0,
    status: "normal",
    violationCount: 0,
    errorMsg: "",
  }));

  // 模拟分析进度（自动完成）
  analysisTasks.value.forEach((task, index) => {
    const timer = setInterval(() => {
      task.progress += Math.floor(Math.random() * 10);
      if (task.progress >= 100) {
        task.progress = 100;
        clearInterval(timer);

        // 模拟分析结果
        setTimeout(() => {
          task.status = "success";
          task.violationCount = Math.floor(Math.random() * 5) + 1; // 1-5处违章

          // 最后一个任务完成后准备结果数据
          if (index === analysisTasks.value.length - 1) {
            prepareResultData();
          }
        }, 500);
      }
    }, 200);
  });
};

// 准备分析结果数据（测试数据）
const prepareResultData = () => {
  // 模拟违章截图
  violationImages.value = [
    {
      url: "https://picsum.photos/800/450?random=1",
      desc: "未佩戴安全帽",
      time: "2025-01-02 10:23:45",
    },
    {
      url: "https://picsum.photos/800/450?random=2",
      desc: "安全绳使用不规范",
      time: "2025-01-02 11:35:18",
    },
    {
      url: "https://picsum.photos/800/450?random=3",
      desc: "违规进入危险区域",
      time: "2025-01-02 14:28:33",
    },
  ];
};

// 生成统计报告（测试数据）
const generateReport = () => {
  resultVisible.value = true;

  // 生成报告基础数据
  reportDate.value = new Date().toLocaleString();
  reportData.value = {
    videoCount: uploadFiles.value.length,
    violationTotal: 8,
    violationTypes: {
      未佩戴安全帽: 3,
      安全绳使用不规范: 2,
      违规进入危险区域: 2,
      未穿工作服: 1,
    },
    analysisDuration: "00:12:45",
    area: "中国石油XX作业区",
    detailDesc:
      "本次共分析作业记录仪视频3个，总时长2小时15分钟，发现各类违章行为共计8处。其中未佩戴安全帽问题最为突出，占比37.5%，主要集中在高空作业区域。建议加强现场安全监督，对相关人员进行安全培训。",
  };

  // 构建表格数据
  reportTableData.value = [
    { label: "分析视频总数", value: `${reportData.value.videoCount} 个` },
    { label: "违章总数", value: `${reportData.value.violationTotal} 处` },
    {
      label: "违章类型",
      value: Object.entries(reportData.value.violationTypes)
        .map(([type, count]) => `${type}：${count} 处`)
        .join("；"),
    },
    { label: "分析时长", value: reportData.value.analysisDuration },
    { label: "涉及区域", value: reportData.value.area },
  ];

  ElMessage.success("统计报告生成成功！");
};

// 分享至中油即时通（模拟）
const shareToIm = () => {
  ElMessageBox.prompt(
    "请输入中油即时通分享对象（可输入用户名/部门/群组）",
    "分享报告",
    {
      confirmButtonText: "确认分享",
      cancelButtonText: "取消",
      inputPlaceholder: "例如：安全管理部/张三",
    },
  ).then(({ value }) => {
    setTimeout(() => {
      ElMessage.success(`报告已成功分享至中油即时通：${value}`);
    }, 800);
  });
};

// 下载报告（模拟）
const downloadReport = () => {
  ElMessage.info("报告正在生成下载文件，请稍候...");
  setTimeout(() => {
    ElMessage.success("报告下载成功！");
  }, 1000);
};

// 打印报告（模拟）
const printReport = () => {
  ElMessage.info("正在准备打印报告...");
  setTimeout(() => {
    ElMessage.success("报告打印任务已提交！");
  }, 800);
};

// 页面加载时可选自动加载测试数据（可选）
// onMounted(() => {
//   loadTestData()
// })
onMounted(() => {});
</script>

<style lang="scss" scoped>
// 基础变量定义 - 深色模式
$primary-color: #409eff;
$text-color: #e5e9f0; // 主文本色-浅灰
$text-color-secondary: #a5b4cc; // 次要文本色-中灰
$text-color-tertiary: #6b7280; // 三级文本色-深灰
$border-color: #374151; // 边框色-深灰
$bg-color: #111827; // 全局背景色-深蓝黑
$bg-color-card: #1f2937; // 卡片背景色-深灰蓝
$bg-color-hover: #374151; // 悬停背景色
$card-shadow: 0 2px 12px rgba(0, 0, 0, 0.2); // 阴影加深
$gap-base: 8px;
$gap-medium: 16px;
$gap-large: 20px;
$gap-xlarge: 24px;

// 全局样式
.law-enforcement-container {
  background-color: $bg-color;
  height: 100%;
  color: $text-color;

  .page-header {
    margin-bottom: $gap-large;

    h2 {
      margin: 0;
      color: $text-color;
      font-size: 20px;
      font-weight: 600;
    }
  }

  .main-content {
    height: 100%;
    display: flex;
    flex-direction: column;
    gap: $gap-large;
    padding: $gap-large;

    .card {
      background: $bg-color-card;
      border-radius: 8px;
      box-shadow: $card-shadow;
      padding: $gap-large;
      border: 1px solid $border-color;
    }

    .section-title {
      font-size: 16px;
      font-weight: 600;
      color: $text-color;
      margin-bottom: $gap-medium;
      display: flex;
      align-items: center;
      gap: $gap-base;
      padding-bottom: $gap-base;
      border-bottom: 1px solid $border-color;
    }

    // 上传区域样式
    .upload-section {
      .upload-content {
        width: 100%;

        .manual-upload-btn {
          margin: $gap-medium 0;
        }

        .upload-file-list {
          margin-top: $gap-large;
        }

        .upload-actions {
          margin-top: $gap-large;
          display: flex;
          gap: 10px;
        }
      }
    }

    // 分析进度区域样式
    .analysis-section {
      display: flex;
      flex-direction: column;
      gap: $gap-medium;

      .analysis-task {
        margin-bottom: $gap-medium;

        .task-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: $gap-base;

          .task-name {
            font-size: 14px;
            color: $text-color-secondary;
          }

          .task-percent {
            font-size: 14px;
            font-weight: 600;
            color: $primary-color;
          }
        }

        .task-status {
          margin-top: $gap-base;
        }
      }

      .analysis-actions {
        margin-top: $gap-large;
        text-align: right;
      }
    }

    // 结果展示区域样式
    .result-section {
      .result-content {
        display: flex;
        flex-direction: column;
        gap: $gap-xlarge;

        .section-subtitle {
          font-size: 14px;
          font-weight: 600;
          color: $text-color;
          margin-bottom: $gap-medium / 1.5;
        }

        // 违章截图样式
        .violation-images {
          .image-grid {
            display: grid;
            grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
            gap: $gap-medium;

            .image-item {
              border: 1px solid $border-color;
              border-radius: 4px;
              overflow: hidden;
              background-color: $bg-color;

              img {
                width: 100%;
                height: 200px;
                object-fit: cover;
              }

              .image-desc {
                padding: $gap-base $gap-medium / 1.5;
                font-size: 14px;
                font-weight: 600;
                color: $text-color;
                background-color: rgba(0, 0, 0, 0.1);
              }

              .image-time {
                padding: 0 $gap-medium / 1.5 $gap-base;
                font-size: 12px;
                color: $text-color-tertiary;
              }
            }
          }
        }

        // 违章视频样式
        .violation-video {
          margin-top: $gap-medium;

          .video-player {
            border-radius: 4px;
            border: 1px solid $border-color;
            background-color: #000;
          }
        }

        // 报告区域样式
        .report-section {
          .report-card {
            border: 1px solid $border-color;
            border-radius: 8px;
            padding: $gap-large;
            background-color: $bg-color;

            .report-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: $gap-medium;
              padding-bottom: $gap-medium;
              border-bottom: 1px solid $border-color;

              h3 {
                margin: 0;
                font-size: 16px;
                color: $text-color;
              }

              .report-date {
                font-size: 12px;
                color: $text-color-tertiary;
              }
            }

            .report-content {
              .report-desc {
                h4 {
                  margin: 16px 0 8px 0;
                  font-size: 14px;
                  color: $text-color;
                }

                p {
                  margin: 0;
                  line-height: 1.6;
                  color: $text-color-secondary;
                }
              }
            }
          }

          .report-actions {
            margin-top: $gap-large;
            display: flex;
            gap: 10px;
            justify-content: flex-end;
          }
        }
      }
    }
  }
}

// Element Plus 样式覆盖 - 深色模式适配
:deep {
  // 上传组件
  .el-upload-dragger {
    width: 100%;
    height: 200px;
    background-color: $bg-color;
    border: 1px dashed $border-color;
    color: $text-color-secondary;

    &:hover {
      background-color: $bg-color-hover;
    }
  }

  .el-upload__text {
    color: $text-color-secondary;
  }

  .el-upload__tip {
    color: $text-color-tertiary;
  }

  // 表格组件
  .el-table {
    --el-table-header-text-color: $text-color;
    --el-table-row-hover-bg-color: $bg-color-hover;
    --el-table-text-color: $text-color-secondary;
    --el-table-border-color: $border-color;
    --el-table-bg-color: $bg-color-card;
    --el-table-header-text-color: $text-color;
    --el-table-header-bg-color: $bg-color;

    background-color: $bg-color-card;
    color: $text-color;

    .el-table__header-wrapper {
      th {
        background-color: rgba(0, 34, 102, 0.8) !important;
        color: $text-color !important;
        border-bottom-color: $border-color !important;
      }
    }

    .el-table__body-wrapper {
      td {
        background-color: rgba(1, 17, 50, 0.9) !important;
        color: $text-color-secondary !important;
        border-bottom-color: $border-color !important;
      }

      tr:hover td {
        background-color: $bg-color-hover !important;
      }
    }
  }

  // 按钮组件
  .el-button {
    --el-button-text-color: $text-color;
    --el-button-disabled-text-color: $text-color-tertiary;
    --el-button-disabled-bg-color: $bg-color;
    --el-button-disabled-border-color: $border-color;
  }

  // 进度条
  .el-progress-bar__inner {
    background-color: $primary-color;
  }

  .el-progress-bar__outer {
    background-color: $bg-color;
    border: 1px solid $border-color;
  }

  // 标签组件
  .el-tag {
    --el-tag-text-color: $text-color;
  }

  // 消息框
  .el-message-box {
    --el-message-box-bg-color: $bg-color-card;
    --el-message-box-title-color: $text-color;
    --el-message-box-content-color: $text-color-secondary;
    --el-message-box-border-color: $border-color;
  }

  // 图片预览器
  .el-image-viewer__wrapper {
    z-index: 9999;
    background-color: rgba(0, 0, 0, 0.9);
  }

  // 图标颜色
  .el-icon {
    color: $text-color;
  }
}
</style>
